import React, { ForwardedRef, useEffect, useMemo } from 'react';
import CustomShaderMaterial from '../CustomShaderMaterial';
import {
  BackSide,
  Color,
  LinearFilter,
  LinearMipMapLinearFilter,
  RepeatWrapping,
  ShaderMaterial,
  Texture
} from 'three';
import { Colors } from '../../../data/Colors';
import { useAssetContext } from '../../../context/AssetContext';

type Props = {
  lineColor?: number,
  bgColor?: number,
  numSegments?: number,
};

const BackgroundMaterial = React.forwardRef(({
  lineColor, bgColor, numSegments
}: Props, ref?: ForwardedRef<ShaderMaterial>
) =>
{
  const assets = useAssetContext();
  const uniforms = useMemo(() =>
  {
    return {
      lineColor: { value: new Color() },
      bgColor: { value: new Color(1, 1, 1) },
      tiling: { value: 1.0 },
      gridTexture: { value: assets.get('gridTexture') }
    };
  }, [ assets ]);

  useEffect(() =>
  {
    const tex = assets.get('gridTexture') as Texture;
    tex.wrapS = RepeatWrapping;
    tex.wrapT = RepeatWrapping;
    tex.minFilter = LinearMipMapLinearFilter;
    tex.magFilter = LinearFilter;
  }, [ assets ]);

  useEffect(() =>
  {
    uniforms.lineColor.value.set(lineColor || Colors.NeutralGrey);
  }, [ uniforms, lineColor ]);

  useEffect(() =>
  {
    uniforms.bgColor.value.set(bgColor || Colors.White);
  }, [ uniforms, bgColor ]);

  useEffect(() =>
  {
    uniforms.tiling.value = numSegments || 40;
  }, [ uniforms, numSegments ]);

  return <CustomShaderMaterial
    fragmentID="backgroundFragmentShader"
    vertexID="backgroundVertexShader"
    uniforms={uniforms}
    depthWrite={false}
    side={BackSide}
    ref={ref}
  />;
});

BackgroundMaterial.displayName = 'BackgroundMaterial';

export default BackgroundMaterial;